import type { EpgInfo } from '@setplex/tria-api'
import type { TFunction } from '~/core/i18n'
import type { ProgramEpgInfo } from '../index.h'

import { differenceInCalendarDays, isToday, isTomorrow } from 'date-fns'
import { t } from '~/core/i18n'
import {
  DATE_FORMATS,
  asDate,
  formatStartTime,
  formatTimeRangeUnified,
  formatTimeUnified,
  isOvernightMidnightCrossing,
} from '~/shared/helpers/date'

/**
 * key for upcoming programs map
 */
export const getMapKeyForProgram = ({
  epgId,
  title,
  startMs,
}: Pick<EpgInfo, 'epgId' | 'title' | 'startMs'>): string =>
  `${epgId}|${title}|${startMs}`

export const formatProgramEpgToFetchParams = ({
  epgId,
  title,
  startMs,
}: EpgInfo) => ({
  epgId: String(epgId),
  programTitle: String(title),
  start: Number(startMs),
})

export const getFormattedSubtitle = (episode?: string, subtitle?: string) => {
  return episode && subtitle ? `${episode}: ${subtitle}` : subtitle || episode
}

export const getProgramDayLabel = (
  start: string | number | Date,
  translate: TFunction = t
) => {
  const startDate = asDate(start)
  if (isToday(startDate)) return translate('today')
  if (isTomorrow(startDate)) return translate('tomorrow')
  return formatStartTime(startDate, DATE_FORMATS.DD_MMM)
}

export const getProgramStartDay = (start: string) => getProgramDayLabel(start)

/** Midnight crossing: day/label before both start and end times. */
export const formatMidnightCrossingEpgRange = (
  startMs: number,
  stopMs: number,
  translate: TFunction = t
) =>
  translate('program-start-end-time', {
    lng: 'date',
    startDate: `${getProgramDayLabel(startMs, translate)}, ${formatTimeUnified(startMs)}`,
    endDate: `${getProgramDayLabel(stopMs, translate)}, ${formatTimeUnified(stopMs)}`,
  })

/**
 * Single EPG label string: "Tomorrow, 7:30 AM - 8:30 AM" or
 * "Tomorrow, 11:30 PM - 25 Apr, 1:00 AM" for midnight crossing.
 * Multi-day non-midnight ranges already include dates in formatTimeRangeUnified.
 */
export const formatEpgProgramRangeLabel = (
  startMs: number,
  stopMs: number,
  translate: TFunction = t
) => {
  if (isOvernightMidnightCrossing(startMs, stopMs)) {
    return formatMidnightCrossingEpgRange(startMs, stopMs, translate)
  }

  const range = formatTimeRangeUnified(startMs, stopMs)
  if (differenceInCalendarDays(asDate(stopMs), asDate(startMs)) > 0) {
    return range
  }

  return `${getProgramDayLabel(startMs, translate)}, ${range}`
}

export const formatProgram = (program: Readonly<EpgInfo>): ProgramEpgInfo => {
  const start = new Date(program.actualStartMs).toString()
  const stop = new Date(program.actualStopMs).toString()
  return {
    title: program.title,
    subtitle: getFormattedSubtitle(program.episode, program.subtitle),
    episode: program.episode,
    date: program.date,
    actors: program.actors,
    directors: program.directors,
    categories: program.categories,
    start,
    stop,
    description: program.description,
    icon: program.icon,
    rating: program.rating,
    labels: [
      formatEpgProgramRangeLabel(program.actualStartMs, program.actualStopMs),
    ],
  }
}
